import { groupBy, mapBy } from '@go-shared/lib/functional';
import { dateToIsoUTC, formatTimeRange, isoLocal } from '@go-shared/lib/time';
import {
  ReservableEventTemplateSnapshot,
  ReservableEventWithTimeslots,
} from '@go-shared/types/reservable';
import { Reservation } from '@go-shared/types/reservation';
import { User } from '@go-shared/types/user';
import { FormikProps } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { BubbleButton, BubbleCurrency, BubbleFormGroup, BubbleHintSelect } from './BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';
import BubbleTable from './BubbleUI/BubbleTable';
import OccurrenceFiller from './OccurrenceFiller';
import {
  ReservationFormikForm,
  ReservationFormikOccurrence,
  ReservationFormikProps,
} from './Formik/ReservationFormik';
import { buildReserveSlotKey, buildResourceSlotKeyPart } from '@go-shared/lib/reserve';
import { ObjectId } from 'bson';
import { ReserveScheduleSlotPriceOverrides } from '@go-shared/types/reserveSchedule';
import { formatCentsToUSD } from '@go-shared/lib/utils';

export interface ReservationDateTimeFillerProps {
  type: ReservationFormikProps['type'];
  users: User[];
  eventSnapshot: ReservableEventTemplateSnapshot;
  formik: FormikProps<ReservationFormikForm>;
  reservation?: Reservation;
  readOnly?: boolean;
  reservableDetails?: ReservableEventWithTimeslots;
  reservableDetailsLoading?: boolean;
}

interface OccurrenceTableItem {
  key: string; // string idx used as key
  occurrence: string;
  date?: string;
  time?: string;
  rental?: string;
  hasErrors: boolean;
  _isMain?: boolean;
  _occ: ReservationFormikOccurrence;
  _idx: number; // -1 for the main row
}

export default function ReservationDateTimeFiller(props: ReservationDateTimeFillerProps) {
  const { formik, eventSnapshot, users, readOnly, reservableDetails, reservableDetailsLoading } =
    props;
  const { values, setFieldValue } = formik;

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const ticketQuantities = Object.values(groupBy(values.tickets, 'ticketId')).map(group => ({
    ticket: { _id: group[0].ticketId },
    quantity: group.length,
  }));

  const ticketsMap = mapBy(eventSnapshot.reservables.tickets, t => t._id);

  const mainOccurrence: ReservationFormikOccurrence = {
    date: values.date,
    time: values.time,
    duration: values.duration,
    host: values.host,
    resource: values.resource,
  };

  // used to reset the calendar
  const originalMainValues: Partial<ReservationFormikForm> | undefined = props.reservation
    ? {
        date: props.reservation.date
          ? dateToIsoUTC(props.reservation.date)
          : reservableDetails?.timeslots[0]?.date
            ? dateToIsoUTC(reservableDetails?.timeslots[0]?.date)
            : isoLocal(),
        time: props.reservation.time ?? undefined,
        duration: props.reservation.duration ?? undefined,
        host: props.reservation.host ?? undefined,
        resource: props.reservation.resource ?? undefined,
        priceOverrides: props.reservation.priceOverrides,
      }
    : undefined;

  function findNextOccurrence(): ReservationFormikOccurrence {
    const usedDates = new Set([
      values.date,
      ...(values.additionalOccurrences ?? []).map(o => o.date),
    ]);

    const allSlots = (reservableDetails?.timeslots ?? [])
      .filter(s => s.spotsLeft > 0 && dateToIsoUTC(s.date) > values.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const idealSlotKey = values.time
      ? buildResourceSlotKeyPart(
          { resource: values.resource, host: values.host },
          values.time,
          values.duration
        )
      : undefined;

    const match = allSlots.find(s => {
      const d = dateToIsoUTC(s.date);
      const slotKey = buildResourceSlotKeyPart(
        { resource: s.resource, host: s.host },
        s.time,
        s.duration
      );
      return !usedDates.has(d) && slotKey === idealSlotKey;
    });

    return {
      _id: new ObjectId().toString(),
      date: match ? dateToIsoUTC(match.date) : values.date,
      time: match?.time,
      duration: match?.duration,
      host: match?.host,
      resource: match?.resource,
    };
  }

  function occurrenceTimeLabel(occ: ReservationFormikOccurrence) {
    if (!occ.time) return undefined;

    const timeRange = formatTimeRange(occ.time, occ.duration);

    const matchingKey = buildReserveSlotKey(
      { date: occ.date, resource: occ.resource, host: occ.host },
      occ.time,
      occ.duration
    );

    const matchingSlot = reservableDetails?.timeslots.find(
      s =>
        buildReserveSlotKey(
          { date: dateToIsoUTC(s.date), resource: s.resource, host: s.host },
          s.time,
          s.duration
        ) === matchingKey
    );

    return matchingSlot?.label ? `${matchingSlot.label} (${timeRange})` : timeRange;
  }

  const occurrences = values.additionalOccurrences ?? [];

  const mainTableItem: OccurrenceTableItem = {
    key: 'main',
    occurrence: 'Main',
    date: mainOccurrence.date,
    time: occurrenceTimeLabel(mainOccurrence),
    rental: ticketsMap[mainOccurrence.resource ?? '']?.name,
    hasErrors: !mainOccurrence.time,
    _isMain: true,
    _occ: mainOccurrence,
    _idx: -1,
  };

  const tableItems: OccurrenceTableItem[] = occurrences.map((occ, idx) => ({
    key: occ._id ?? String(idx),
    occurrence: `Occurrence ${idx + 2}`,
    date: occ.date,
    time: occurrenceTimeLabel(occ),
    rental: ticketsMap[occ.resource ?? '']?.name,
    hasErrors: !occ.time,
    _occ: occ,
    _idx: idx,
  }));

  const selectedOcc =
    selectedKey && selectedKey !== 'main'
      ? occurrences.find(o => o._id === selectedKey)
      : undefined;

  function makeRowTdProps(
    data: OccurrenceTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const isExpanded = data.key === selectedKey;
    return {
      ...fieldTdProps,
      className: [fieldTdProps?.className, isExpanded ? 'bg-bubblegum-lightest' : undefined]
        .filter(Boolean)
        .join(' '),
    };
  }

  const tableFields: BubbleTableFields<OccurrenceTableItem>[] = [
    { key: 'occurrence' },
    { key: 'date' },
    { key: 'time' },
    { key: 'rental' },
    { key: 'hasErrors', label: '', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const tableTemplates: BubbleTableTemplate<OccurrenceTableItem>[] = [
    { key: 'occurrence', tdProps: makeRowTdProps },
    { key: 'date', tdProps: makeRowTdProps },
    { key: 'time', tdProps: makeRowTdProps },
    { key: 'rental', tdProps: makeRowTdProps },
    {
      key: 'hasErrors',
      tdProps: makeRowTdProps,
      render: item =>
        item.hasErrors ? (
          <div className="flex items-center text-crimson font-semibold">
            <FontAwesomeIcon icon="exclamation-triangle" />
            <span className="ml-1">Incomplete</span>
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      key: 'actions',
      tdProps: makeRowTdProps,
      render: item => (
        <div className="flex items-center">
          <BubbleButton
            variant="navy"
            className="p-1-i"
            inverse
            icon="edit"
            onClick={() => setSelectedKey(item.key === selectedKey ? null : item.key)}
          />
          {!readOnly && !item._isMain && (
            <BubbleButton
              variant="crimson"
              inverse
              className="p-2-i"
              icon="trash-alt"
              onClick={() => {
                setFieldValue(
                  'additionalOccurrences',
                  occurrences.filter(o => o._id !== item._occ._id)
                );
                setSelectedKey(null);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  const priceOverrideOpts = eventSnapshot.reservables.tickets.map(t => t._id);

  function getAvailablePriceOverrideOpts(
    overrides: ReserveScheduleSlotPriceOverrides | undefined,
    currentKey?: string
  ) {
    return priceOverrideOpts.filter(
      o =>
        !Object.keys(overrides || {})
          .filter(k => k !== currentKey)
          .includes(o)
    );
  }

  function nextAvailablePriceOverrideOpt(overrides: ReserveScheduleSlotPriceOverrides | undefined) {
    return getAvailablePriceOverrideOpts(overrides)[0];
  }

  return (
    <div className="grid grid-cols-1 gap-8">
      <BubbleFormGroup label="Occurrences">
        <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
          <div className="text-xs text-slate italic">
            Click the main row to edit the primary date and time. Add extra occurrences below.
          </div>
          <BubbleTable
            responsive
            striped
            slim
            hideHeader
            bordered
            items={[mainTableItem, ...tableItems]}
            fields={tableFields}
            templates={tableTemplates}
            detailsKey={selectedKey ?? ''}
            onRowClick={item => setSelectedKey(item.key === selectedKey ? null : item.key)}
            detailsClassName="p-4 bg-bubblegum-lightest"
            detailsRender={() => {
              if (selectedKey === 'main') {
                return (
                  <div className="w-full bg-eggshell shadow p-3 border">
                    <OccurrenceFiller
                      values={mainOccurrence}
                      onChange={next => formik.setValues({ ...values, ...next })}
                      onClear={() =>
                        formik.setValues({
                          ...values,
                          ...(originalMainValues || { date: isoLocal() }),
                        })
                      }
                      onSetPriceOverrides={priceOverrides =>
                        setFieldValue('priceOverrides', priceOverrides)
                      }
                      ticketQuantities={ticketQuantities}
                      users={users}
                      eventSnapshot={eventSnapshot}
                      reservableDetails={reservableDetails}
                      reservableDetailsLoading={reservableDetailsLoading}
                      readOnly={readOnly}
                    />
                  </div>
                );
              }
              return selectedOcc ? (
                <div className="w-full bg-eggshell shadow p-3 border">
                  <OccurrenceFiller
                    values={selectedOcc}
                    onChange={next => {
                      setFieldValue(
                        'additionalOccurrences',
                        occurrences.map(o => (o._id === selectedOcc?._id ? next : o))
                      );
                    }}
                    ticketQuantities={ticketQuantities}
                    users={users}
                    eventSnapshot={eventSnapshot}
                    reservableDetails={reservableDetails}
                    reservableDetailsLoading={reservableDetailsLoading}
                    readOnly={readOnly}
                  />
                </div>
              ) : null;
            }}
          />
          {!readOnly && (
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon="plus"
              onClick={() => {
                const newOcc = findNextOccurrence();
                setFieldValue('additionalOccurrences', [...occurrences, newOcc]);
                setSelectedKey(newOcc._id!);
              }}
            >
              Add Occurrence
            </BubbleButton>
          )}
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Price Overrides">
        <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
          <div className="text-xs text-slate italic">
            Auto populates from the timeslot selected if exists. Use to override the price for
            specific tickets for this reservation.
          </div>
          {Object.entries(values.priceOverrides || {}).map(([key, value]) => (
            <div key={key} className="w-full grid grid-cols-2 gap-4 items-start">
              <BubbleFormGroup label="Ticket">
                <BubbleHintSelect
                  value={key}
                  allowUndefined={false}
                  options={getAvailablePriceOverrideOpts(values.priceOverrides, key)}
                  labelExtractor={opt =>
                    ticketsMap[opt] ? `Ticket: ${ticketsMap[opt].name}` : opt
                  }
                  onValue={newKey => {
                    const next: ReserveScheduleSlotPriceOverrides = {
                      ...values.priceOverrides,
                      [newKey]: value,
                    };
                    delete next[key];
                    setFieldValue('priceOverrides', next);
                  }}
                />
              </BubbleFormGroup>
              <div className="flex items-center">
                <BubbleFormGroup
                  label={`Price${ticketsMap[key] ? ` — base: ${formatCentsToUSD(ticketsMap[key].price)}` : ''}`}
                  className="flex-grow"
                >
                  <BubbleCurrency
                    block
                    value={value}
                    onValue={val => {
                      if (val === undefined) return;
                      setFieldValue('priceOverrides', { ...values.priceOverrides, [key]: val });
                    }}
                    readOnly={readOnly}
                  />
                </BubbleFormGroup>
                {!readOnly && (
                  <BubbleButton
                    style={{ alignSelf: 'end' }}
                    variant="crimson"
                    inverse
                    className="-mr-3"
                    icon="trash-alt"
                    onClick={() => {
                      const next = { ...values.priceOverrides };
                      delete next[key];
                      setFieldValue('priceOverrides', next);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
          {!readOnly && (
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon="plus"
              disabled={!nextAvailablePriceOverrideOpt(values.priceOverrides)}
              onClick={() => {
                const nextKey = nextAvailablePriceOverrideOpt(values.priceOverrides);
                if (!nextKey) return;
                setFieldValue('priceOverrides', {
                  ...(values.priceOverrides || {}),
                  [nextKey]: ticketsMap[nextKey]?.price ?? 0,
                });
              }}
            >
              Add Price Override
            </BubbleButton>
          )}
        </div>
      </BubbleFormGroup>
    </div>
  );
}
